import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { toast } from '../hooks/use-toast';
import emailjs from '@emailjs/browser';

const Contact = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Send email using EmailJS
      // Match template variables: {{title}}, {{name}}, {{email}}, {{message}}
      const templateParams = {
        title: language === 'de' ? 'Neue Nachricht von Website' : 'New Message from Website',
        name: formData.name,
        email: formData.email,
        message: formData.message
      };

      console.log('Sending email with params:', templateParams);
      console.log('Service ID:', process.env.REACT_APP_EMAILJS_SERVICE_ID);
      console.log('Template ID:', process.env.REACT_APP_EMAILJS_TEMPLATE_ID);

      const result = await emailjs.send(
        process.env.REACT_APP_EMAILJS_SERVICE_ID,
        process.env.REACT_APP_EMAILJS_TEMPLATE_ID,
        templateParams,
        process.env.REACT_APP_EMAILJS_PUBLIC_KEY
      );

      console.log('Email sent successfully:', result);
      
      toast({
        title: t.contact.success,
        description: language === 'de' 
          ? 'Ich werde mich in Kürze bei Ihnen melden.'
          : 'I will get back to you shortly.',
      });
      
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      console.error('Error sending email:', error);
      console.error('Error details:', error.text || error.message);
      console.error('Error status:', error.status);
      
      toast({
        title: t.contact.error,
        description: language === 'de'
          ? `Fehler: ${error.text || 'Bitte versuchen Sie es erneut oder kontaktieren Sie mich direkt per E-Mail.'}`
          : `Error: ${error.text || 'Please try again or contact me directly via email.'}`,
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="brutalist-section">
      <div className="brutalist-container">
        <div data-reveal="out" className="grid md:grid-cols-[0.95fr_1.05fr] gap-10 items-start">
          <div>
            <p className="brutalist-kicker">{t.contact.title}</p>
            <h2 className="brutalist-title mt-4">{t.contact.subtitle}</h2>
            <p className="brutalist-subtitle mt-4">{t.contact.intro}</p>

            <div className="mt-10 space-y-4">
              <div
                data-reveal="out"
                style={{ '--reveal-delay': '0ms' }}
                className="brutalist-card p-5 transition-colors duration-100 ease-linear hover:bg-muted hover:border-accent"
              >
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 border border-foreground bg-accent text-accent-foreground flex items-center justify-center flex-shrink-0">
                    <Mail className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="font-mono text-xs tracking-[0.35em] uppercase text-muted-foreground">Email</p>
                    <a
                      href="mailto:sbardella@sbardella.de"
                      className="mt-3 inline-flex text-lg hover:underline underline-offset-4"
                    >
                      sbardella@sbardella.de
                    </a>
                  </div>
                </div>
              </div>

              <div
                data-reveal="out"
                style={{ '--reveal-delay': '70ms' }}
                className="brutalist-card p-5 transition-colors duration-100 ease-linear hover:bg-muted hover:border-accent"
              >
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 border border-foreground bg-accent text-accent-foreground flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="font-mono text-xs tracking-[0.35em] uppercase text-muted-foreground">
                      {language === 'de' ? 'Telefon' : 'Phone'}
                    </p>
                    <a
                      href="tel:+491735934266"
                      className="mt-3 inline-flex text-lg hover:underline underline-offset-4"
                    >
                      +49 173 593 4266
                    </a>
                  </div>
                </div>
              </div>

              <div
                data-reveal="out"
                style={{ '--reveal-delay': '140ms' }}
                className="brutalist-card p-5 transition-colors duration-100 ease-linear hover:bg-muted hover:border-accent"
              >
                <div className="flex items-start gap-4">
                  <div className="h-12 w-12 border border-foreground bg-accent text-accent-foreground flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6" />
                  </div>
                  <div>
                    <p className="font-mono text-xs tracking-[0.35em] uppercase text-muted-foreground">
                      {language === 'de' ? 'Standort' : 'Location'}
                    </p>
                    <p className="mt-3 text-muted-foreground leading-relaxed">
                      Benrodestraße 21<br />
                      40597 Düsseldorf<br />
                      Deutschland
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div data-reveal="out" style={{ '--reveal-delay': '100ms' }} className="brutalist-card p-6 md:p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name" className="mb-2 block">
                  {t.contact.name}
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  placeholder={language === 'de' ? 'Ihr Name' : 'Your Name'}
                />
              </div>

              <div>
                <Label htmlFor="email" className="mb-2 block">
                  {t.contact.email}
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  placeholder={language === 'de' ? 'ihre.email@beispiel.de' : 'your.email@example.com'}
                />
              </div>

              <div>
                <Label htmlFor="message" className="mb-2 block">
                  {t.contact.message}
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  rows={5}
                  placeholder={language === 'de' ? 'Ihre Nachricht...' : 'Your message...'}
                />
              </div>

              <Button type="submit" disabled={isSubmitting} className="w-full" size="lg">
                {isSubmitting ? t.contact.sending : t.contact.send}
                {!isSubmitting && <Send className="ml-2 w-5 h-5" />}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
